/**
 * One chart look for the trainee pages, replacing chart.js's defaults (teal fill, a dense
 * tick grid, a legend box), which read as dated next to the restyled cards.
 *
 * Radar (영역별 분석 / 성과, session report and dashboard): round light rings, no 20/40/60/
 * 80/100 scale, the site's blue, each axis labelled with its own value — the scale's job,
 * done where the eye already is.
 * Line (dashboard 평가 내역, 영역별 추이): the same blue with white points and a faint fill,
 * a light grid at 0/50/100 only, dates without repeats, no legend unless there is a
 * comparison line.
 */
import { tagLabel } from "../features/evaluation_sessions/evaluationRules";

export const FONT = '-apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo", "Pretendard Variable", "Pretendard", "Noto Sans KR", sans-serif';

/** Axis label: Apple's stage name, then the Korean gloss with the value ("니즈 파악 40%"). */
export const radarLabel = (tag, pct) => {
  const lines = [].concat(tagLabel(tag));
  const value = pct == null ? "" : ` ${Math.round(pct)}%`;
  return lines.length > 1 ? [lines[0], `${lines[1]}${value}`] : [lines[0], value.trim()];
};

/** The trainee's own result: blue fill, blue outline, white points with a blue rim. */
export const radarDataset = (data, label = "기준 충족 %") => ({
  label,
  data,
  backgroundColor: "rgba(0, 122, 255, 0.14)",
  borderColor: "#007AFF",
  borderWidth: 2,
  pointRadius: 3.5,
  pointHoverRadius: 5,
  pointBackgroundColor: "#ffffff",
  pointBorderColor: "#007AFF",
  pointBorderWidth: 2,
});

/** A comparison line (e.g. 회사 평균): dashed orange outline, no fill, no points. */
export const radarComparisonDataset = (data, label) => ({
  label,
  data,
  backgroundColor: "transparent",
  borderColor: "#FF9500",
  borderWidth: 1.5,
  borderDash: [4, 4],
  pointRadius: 0,
  pointHoverRadius: 3,
});

const TOOLTIP = {
  backgroundColor: "#1d1d1f",
  cornerRadius: 8,
  padding: 8,
  displayColors: false,
  titleFont: { family: FONT, size: 12, weight: "600" },
  bodyFont: { family: FONT, size: 12 },
};

export const radarOptions = (extra = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  layout: { padding: 4 },
  scales: {
    r: {
      min: 0,
      max: 100,
      ticks: { display: false, stepSize: 25 },
      grid: { circular: true, color: "rgba(0, 0, 0, 0.06)" },
      angleLines: { color: "rgba(0, 0, 0, 0.05)" },
      pointLabels: { color: "#424245", padding: 8, font: { family: FONT, size: 11, weight: "600" } },
    },
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      ...TOOLTIP,
      callbacks: {
        title: (items) => [].concat(items[0]?.label || "").join(" "),
        label: (ctx) => `${ctx.dataset.label}: ${Math.round(ctx.raw)}%`,
      },
    },
  },
  ...extra,
});

/** The trainee's line: blue, white points with a blue rim, a faint fill under it. */
export const lineDataset = (data, label) => ({
  label,
  data,
  borderColor: "#007AFF",
  borderWidth: 2,
  backgroundColor: "rgba(0, 122, 255, 0.08)",
  fill: "start",
  tension: 0.3,
  pointRadius: 3,
  pointHoverRadius: 5,
  pointBackgroundColor: "#ffffff",
  pointBorderColor: "#007AFF",
  pointBorderWidth: 2,
});

/** A comparison line (회사 평균): dashed orange, no fill, no points. */
export const lineComparisonDataset = (data, label) => ({
  label,
  data,
  borderColor: "#FF9500",
  borderWidth: 1.5,
  borderDash: [4, 4],
  backgroundColor: "transparent",
  fill: false,
  pointRadius: 0,
  tension: 0.3,
});

/**
 * Which x positions get a date: where the date changes (several sessions on one day all
 * read "07/08"), thinned to at most `max` evenly spaced ones. chart.js's own autoSkip can't
 * be combined with that — it picked ticks whose labels were all repeats, leaving one date.
 */
const dateTicks = (labels, max = 6) => {
  const firsts = labels.map((l, i) => (i === 0 || labels[i - 1] !== l ? i : -1)).filter((i) => i >= 0);
  const step = Math.ceil(firsts.length / max);
  return new Set(firsts.filter((_, k) => k % step === 0));
};

/** Options for a 0–100 line over dated labels; a legend appears only when there is more than one line. */
export const lineOptions = (labels, { legend = false, unit = "", ...extra } = {}) => {
  const shown = dateTicks(labels);
  return {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  scales: {
    y: {
      min: 0,
      max: 100,
      ticks: { stepSize: 50, color: "#86868b", font: { family: FONT, size: 11 } },
      grid: { color: "rgba(0, 0, 0, 0.06)" },
      border: { display: false },
    },
    x: {
      grid: { display: false },
      border: { display: false },
      ticks: {
        color: "#86868b",
        font: { family: FONT, size: 11 },
        maxRotation: 0,
        autoSkip: false,
        callback: (value, index) => (shown.has(index) ? labels[index] : ""),
      },
    },
  },
  plugins: {
    legend: legend
      ? { display: true, position: "bottom", labels: { usePointStyle: true, pointStyle: "line", boxWidth: 16, color: "#424245", font: { family: FONT, size: 11 } } }
      : { display: false },
    tooltip: { ...TOOLTIP, callbacks: { label: (ctx) => `${ctx.dataset.label}: ${Math.round(ctx.raw)}${unit}` } },
  },
  ...extra,
  };
};
